// Central route constants and redirect helpers — single source of truth for
// route strings used by middleware (proxy.ts), login pages, auth guards, and
// org-flow pages.  Every file that previously hardcoded these paths should
// import from here instead.

// ---------------------------------------------------------------------------
// Route path constants
// ---------------------------------------------------------------------------

export const ROUTES = {
  HOME: "/",
  DASHBOARD: "/dashboard",
  LOGIN: "/login",
  VERIFY_EMAIL: "/verify-email",
  RESET_PASSWORD: "/reset-password",
  WORKFLOW: "/workflows",
  WORKFLOW_EDIT: "/workflows/edit",
  WORKFLOW_HISTORY: "/workflows/history",
  TEMPLATES: "/templates",
  CONNECTORS: "/connectors",
  INTEGRATIONS: "/integrations",
  AGENTS: "/agents",
  ANALYTICS: "/analytics",
  FEATURE_REQUESTS: "/feature-requests",
  ONBOARDING: "/onboarding",
  SELECT_ORG: "/select-org",
  JOIN_ORG: "/join-org",
  ACCEPT_INVITE: "/invites/accept",
  CLAIM: "/claim",
  ORG_SETTINGS: "/organizations/settings",
  ACCOUNT_SETTINGS: "/account-settings",
  BILLING: "/billing",
  WALLET_MANAGEMENT: "/wallet-management",
  CONTRACTS: "/contracts",
  SITEMAP: "/sitemap",
  // Legacy /sitemap.xml URL — preserved as a sitemap-index for any external
  // consumer (Search Console submissions, third-party tools) that still
  // points here. Served by app/sitemap.xml/route.ts.
  SITEMAP_INDEX_LEGACY: "/sitemap.xml",
  ROBOTS: "/robots.txt",
  LLMS_TXT: "/llms.txt",
  LLMS_FULL_TXT: "/llms-full.txt",
  PRICING: "/pricing",
  ABOUT: "/about",
  COMPANY: "/company",
  CONTACT: "/contact",
  AUTHORS: "/authors",
  BLOG: "/blog",
  PUBLIC_WORKFLOW: "/w",
  SOLUTIONS: "/solutions",
  USE_CASES: "/use-cases",
  CUSTOMERS: "/customers",
  TERMS: "/terms",
  PRIVACY: "/privacy",
  // Auth-only routes without dedicated ROUTES constants elsewhere in the codebase.
  ADMIN: "/admin",
  CLI: "/cli",
  DATABASES: "/databases",
  INCENTIVE: "/incentive",
  ORDERS: "/orders",
  ORGANIZATIONS: "/organizations",
  PLUGINS: "/plugins",
  TX: "/tx",
  V2: "/v2",
  WEBHOOKS: "/webhooks",
  GATE: "/gate",
} as const;

// ---------------------------------------------------------------------------
// Page classification lists
// ---------------------------------------------------------------------------

/** Pages accessible without authentication (middleware allows through). */
export const PUBLIC_PAGES = [
  ROUTES.HOME,
  ROUTES.CONNECTORS,
  ROUTES.FEATURE_REQUESTS,
  ROUTES.INTEGRATIONS,
  ROUTES.TEMPLATES,
  ROUTES.AGENTS,
  ROUTES.SITEMAP,
  ROUTES.SITEMAP_INDEX_LEGACY,
  ROUTES.ROBOTS,
  ROUTES.LLMS_TXT,
  ROUTES.LLMS_FULL_TXT,
  ROUTES.PRICING,
  ROUTES.ABOUT,
  ROUTES.COMPANY,
  ROUTES.CONTACT,
  ROUTES.AUTHORS,
  ROUTES.BLOG,
  ROUTES.PUBLIC_WORKFLOW,
  ROUTES.VERIFY_EMAIL,
  ROUTES.RESET_PASSWORD,
  ROUTES.SOLUTIONS,
  ROUTES.USE_CASES,
  ROUTES.CUSTOMERS,
  ROUTES.TERMS,
  ROUTES.PRIVACY,
  ROUTES.GATE,
] as const;

/**
 * Pages that require authentication. Anonymous visits to a path matching one of
 * these prefixes are redirected to `/login` by the middleware.
 *
 * Anything outside this list is allowed to fall through to Next's router so that
 * unmatched URLs return a real 404 instead of a soft-404 via login redirect.
 */
export const AUTH_PAGES = [
  ROUTES.ACCOUNT_SETTINGS,
  ROUTES.ADMIN,
  ROUTES.ANALYTICS,
  ROUTES.ACCEPT_INVITE,
  ROUTES.BILLING,
  ROUTES.CLI,
  ROUTES.CONTRACTS,
  ROUTES.DASHBOARD,
  ROUTES.DATABASES,
  ROUTES.INCENTIVE,
  ROUTES.JOIN_ORG,
  ROUTES.ONBOARDING,
  ROUTES.ORDERS,
  ROUTES.ORGANIZATIONS,
  ROUTES.PLUGINS,
  ROUTES.SELECT_ORG,
  ROUTES.TX,
  ROUTES.V2,
  ROUTES.WALLET_MANAGEMENT,
  ROUTES.WEBHOOKS,
  ROUTES.WORKFLOW,
] as const;

/** Pages that are part of the org-setup flow (exempt from org redirects). */
export const ORG_FLOW_PAGES = [ROUTES.SELECT_ORG, ROUTES.JOIN_ORG, ROUTES.ACCEPT_INVITE, ROUTES.ONBOARDING] as const;

// ---------------------------------------------------------------------------
// Default post-login destination (single source of truth)
// ---------------------------------------------------------------------------

export const DEFAULT_POST_LOGIN_DESTINATION = ROUTES.WORKFLOW;

// ---------------------------------------------------------------------------
// Helper functions
// ---------------------------------------------------------------------------

/** Build a `/login?redirect=<currentPath>` URL. */
export function buildLoginRedirectUrl(currentPath: string): string {
  return `${ROUTES.LOGIN}?redirect=${encodeURIComponent(currentPath)}`;
}

/**
 * Determine a safe redirect target from the `?redirect` query param.
 *
 * Falls back to `DEFAULT_POST_LOGIN_DESTINATION` when the value is missing,
 * is the root path (`/`), or points back at the login page.
 */
export function getSafeRedirectTarget(redirectParam: string | null): string {
  if (
    redirectParam &&
    redirectParam !== ROUTES.HOME &&
    !redirectParam.startsWith(ROUTES.LOGIN) &&
    redirectParam.startsWith("/") &&
    !redirectParam.startsWith("//")
  ) {
    return redirectParam;
  }
  return DEFAULT_POST_LOGIN_DESTINATION;
}

/**
 * Guard against `javascript:`, `data:`, and other non-http schemes before
 * handing an externally-sourced URL to `<a href>` or `window.open`.
 * Only `http://` and `https://` (protocol-relative `//` also rejected) pass.
 */
export function isSafeHttpUrl(url: unknown): url is string {
  if (typeof url !== "string" || url.length === 0) return false;
  const trimmed = url.trim();
  if (trimmed.startsWith("//")) return false;
  return /^https?:\/\//i.test(trimmed);
}

/** Check whether `pathname` matches a public page (exact or prefix). */
export function isPublicPage(pathname: string): boolean {
  return PUBLIC_PAGES.some(page => {
    if (page === "/") return pathname === "/";
    return pathname === page || pathname.startsWith(`${page}/`);
  });
}

/**
 * Check whether `pathname` matches an authenticated route prefix.
 *
 * Used by the middleware to decide between redirecting anonymous users to
 * `/login` (auth-gated paths) and passing through to Next so unmatched URLs
 * can return a real 404 (everything else).
 */
export function isAuthPage(pathname: string): boolean {
  return AUTH_PAGES.some(page => pathname === page || pathname.startsWith(`${page}/`));
}

/** Check whether `pathname` is part of the org-setup flow. */
export function isOrgFlowPage(pathname: string): boolean {
  return ORG_FLOW_PAGES.some(page => pathname === page);
}
